import * as React from "react";
import * as moment from "moment";
import {Link, LinkProps} from "react-router-dom";
import {
    capitalize,
    getAmountWithoutDecimalDigits,
    getCursorClassName,
    getIsOptionalExtraIncludedText,
    getLink
} from "../../../../../CommonFunctions";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import {InfoModal} from "../../../../../components/modals/InfoModal";
import {AnnualPolicyInfo, GreenCard,} from "@ferry/journey-services";

const Section: React.FC<React.HTMLProps<HTMLDivElement>> = ({className, children}) => (
    <div className={`section ${className || ""}`}>{children}</div>
);

const SectionHeading: React.FC<React.HTMLProps<HTMLDivElement>> = ({className, children}) => (
    <div className={`section-heading text-blue-green font-weight-bold ${className || ""}`}>{children}</div>
);

const SectionContents: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="section-contents">{children}</div>
);

const Detail: React.FC<React.HTMLProps<HTMLDivElement>> = ({children, className}) => (
    <div className={`detail ${className || ""}`}>{children}</div>
);

const DetailKey: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-key">{children}</div>
);

const DetailValue: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-value">{children}</div>
);

const DetailValuePrice: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-value font-weight-bold text-blue-1">{children}</div>
);

const SectionLinks: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="section-links">{children}</div>
);

const PanelLink: React.FC<LinkProps> = ({
                                            id, "aria-label": ariaLabel, to,
                                            children, className
                                        }) => (
    <Link
        id={id}
        className={`text-blue-2 ${className}`}
        aria-label={ariaLabel}
        to={to}
    >{children}</Link>
);

const LinkHighlight: React.FC<React.PropsWithChildren> = ({children}) => (
    <span className="text-blue-1 font-weight-semi-bold text-decoration-underline">{children}</span>
);

const renderWolfExcessTableExplained = (excesses: any, excessType: string) => (
    <table className={`table table-responsive table-striped excess-table ${excessType}`}>
        <thead>
        <tr>
            <th scope="col"/>
            <th scope="col">Compulsory excess</th>
            <th scope="col">Voluntary excess</th>
            <th scope="col">Total</th>
        </tr>
        </thead>
        <tbody>
        <tr>
            <td className="font-weight-semi-bold">Fire</td>
            <td>{getAmountWithoutDecimalDigits(excesses.fireExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.voluntaryExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.totalFire)}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Theft</td>
            <td>{getAmountWithoutDecimalDigits(excesses.theftExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.voluntaryExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.totalTheft)}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Accidental damage</td>
            <td>{getAmountWithoutDecimalDigits(excesses.adExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.voluntaryExcess)}</td>
            <td>{getAmountWithoutDecimalDigits(excesses.totalAd)}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Glass replacement</td>
            <td>{getAmountWithoutDecimalDigits(excesses.windscreenReplacementExcess)}</td>
            <td>-</td>
            <td>{getAmountWithoutDecimalDigits(excesses.windscreenReplacementExcess)}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Glass repair</td>
            <td>{getAmountWithoutDecimalDigits(excesses.windscreenRepairExcess)}</td>
            <td>-</td>
            <td>{getAmountWithoutDecimalDigits(excesses.windscreenRepairExcess)}</td>
        </tr>
        </tbody>
    </table>
);


const ExcessExplanation: React.FC<{ vehicle: any}> = ({
                                                                              vehicle: {annualVehicleExcess}
                                                                          }) => (
    <>
        <p className="font-weight-extra-light mb-4">
            An excess is the amount you play towards any claim you make.
        </p>
        <p className="font-weight-extra-light mb-4">
            You have {getAmountWithoutDecimalDigits(annualVehicleExcess.voluntaryExcess)} voluntary excess and
            {" "}{getAmountWithoutDecimalDigits(annualVehicleExcess.adExcess)} compulsory excess for any accidental damage, fire or theft. See an example
            below.
        </p>
        {renderWolfExcessTableExplained(annualVehicleExcess, "compulsory")}
        <div className="banner info-banner">
            <div className="banner-text">
                * If you decide to use your own repairer rather than one we have recommended then an
                additional <b>{getAmountWithoutDecimalDigits(annualVehicleExcess.nonRecommendedRepairerExcess)}</b> excess will be applied to you your
                total excess (compulsory + voluntary)
            </div>
        </div>
    </>
);

const DriverDetail: React.FC<{
    interactionId: string, vehicleIndex: number, driver: any, isMainDriver: boolean,
    isPolicyHolder: boolean, driverIndex?: number, disableLinks: boolean, canPerformMta: boolean
}> =
    ({interactionId, vehicleIndex, driver, isMainDriver, isPolicyHolder, driverIndex, disableLinks, canPerformMta}) => {

        return (
            <Detail>
                <DetailKey>
                    {`${driver.firstName} ${driver.lastName}`}
                </DetailKey>
                <div className="text-center">
                    {canPerformMta && <PanelLink
                        id={`vehicle-${vehicleIndex}-edit-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                        aria-label="View or edit driver"
                        to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${driverIndex !== undefined ? driverIndex : "PolicyHolder"}/${interactionId}`,
                            disableLinks)}
                        className={getCursorClassName(disableLinks)}
                    >
                        {<LinkHighlight>View / </LinkHighlight>}
                        {isPolicyHolder && <LinkHighlight>Edit details</LinkHighlight>}
                    </PanelLink>
                    }
                    {isPolicyHolder && !canPerformMta && <span>View / Edit details</span>}
                    {!isPolicyHolder && canPerformMta && <PanelLink
                        id={`vehicle-${vehicleIndex}-remove-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                        aria-label="View or edit driver"
                        to={getLink(`/journey/show/product/AnnualMotorInsurance/process/mtaRemoveDriver/removeDriver${driverIndex}EntryPage/${interactionId}`,
                            disableLinks)}
                        className={getCursorClassName(disableLinks)}
                    >
                        <LinkHighlight>Remove driver</LinkHighlight>
                    </PanelLink>}
                    {!isPolicyHolder && !canPerformMta && <span>View / Remove driver</span>}
                </div>
            </Detail>
        );
    }

const DriversSectionContents: React.FC<{
    interaction: any,
    interactionId: string,
    vehicleIndex: number,
    disableLinks: boolean,
    canPerformMta: boolean
}> =
    ({interaction, interactionId, vehicleIndex, disableLinks, canPerformMta}) => {

        const vehicle = interaction.vehicles[vehicleIndex];

        const allDrivers: any[] = [interaction.policyHolder];
        if (interaction.additionalDrivers) {
            allDrivers.push(...interaction.additionalDrivers);
        }

        const vehicleDrivers: any[] = vehicle.driverIds.map(
            (driverId: string) => allDrivers.find(
                (driver: any) => driverId === driver.personId
            )
        );

        const additionalDrivers: any[] = vehicleDrivers.filter(
            (driver: any) => driver.personId !== interaction.policyHolder.personId
        )

        return (
            <SectionContents>
                <DriverDetail
                    interactionId={interactionId}
                    vehicleIndex={vehicleIndex}
                    driver={interaction.policyHolder}
                    isMainDriver={interaction.policyHolder.personId === vehicle.mainDriverId}
                    disableLinks={disableLinks}
                    canPerformMta={canPerformMta}
                    isPolicyHolder={true}
                />
                {additionalDrivers.map(
                    (driver: any, i: number) =>
                        <DriverDetail
                            key={i}
                            interactionId={interactionId}
                            vehicleIndex={vehicleIndex}
                            driver={driver}
                            driverIndex={i}
                            isMainDriver={driver.personId === vehicle.mainDriverId}
                            isPolicyHolder={false}
                            disableLinks={disableLinks}
                            canPerformMta={canPerformMta}
                        />)
                }
            </SectionContents>
        );
    }

interface IWolfYourCarPanelProps {
    interaction: any;
    currentlyActiveInteraction?: any;
    vehicleIndex: number;
    annualPolicyInfo: AnnualPolicyInfo;
    policyEndDate: string;
}

interface IWolfYourCarPanelState {
    greenCards: GreenCard[]
}

export class AnnualYourCarPanel extends React.Component<IWolfYourCarPanelProps, IWolfYourCarPanelState> {

    private readonly vehicle: any;

    private readonly maxDriversCount: number = 6;

    private readonly alertMessage: string | undefined = undefined;

    private readonly interactionToRender: any;

    constructor(props: IWolfYourCarPanelProps) {
        super(props);

        this.interactionToRender = this.props.currentlyActiveInteraction || this.props.interaction;

        if (this.props.annualPolicyInfo.hasDebt) {
            this.alertMessage = "Due to missed payment this action is not allowed";
        }

        this.vehicle = this.interactionToRender.vehicles[this.props.vehicleIndex!];

        this.state = {
            greenCards: []
        };
    }

    public render() {
        const {interaction, vehicleIndex} = this.props;
        const driversCount: number = interaction.vehicles[0].driverIds.length;
        return (
            <div
                id={`your-car-panel-${vehicleIndex}`}
                className="section-panel your-car-panel mb-5"
            >
                <Section className="car-details-section">
                    <div className="car-details-content">
                        <div className="mobile-heading">
                            <div>
                                <div
                                    className="section-heading car-heading text-blue-green font-family-display font-weight-bold mb-0">{`${capitalize(this.vehicle.make)}, ${capitalize(this.vehicle.model)}`}</div>
                                <SectionContents>
                                    <Detail>
                                        <div
                                            className="number-plate-preview px-2 d-block mx-auto mb-2">{this.vehicle.registrationNumber}</div>
                                    </Detail>
                                </SectionContents>
                            </div>
                        </div>
                        <SectionLinks>
                            <Link
                                id={`edit-car-${vehicleIndex}`}
                                className={`btn btn-primary btn-next-no-arrow w-100 ${this.isDisabled() ? "btn-disabled" : ""}`}
                                to={this.isDisabled() ? "" : `/journey/init/product/AnnualMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${interaction._id}`}
                            >
                                Edit car or cover
                            </Link>
                            <p className="mb-0 text-center font-weight-extra-light">
                                or get a quote to replace it
                            </p>
                            <ButtonLink
                                id={`replace-car-${vehicleIndex}`}
                                className={`${interaction._id}`}
                                aria-label="Replace car"
                                to={this.isDisabled() ? "" : `/journey/init/product/AnnualMotorInsurance/process/mtaReplaceCar/preset/${interaction._id}`}
                                disabled={this.isDisabled()}
                                alertMessage={this.alertMessage}
                            >Change this car</ButtonLink>
                        </SectionLinks>
                    </div>
                </Section>
                <Section className="drivers-section">
                    <div className="mobile-heading">
                        <SectionHeading>Drivers details</SectionHeading>
                    </div>
                    <div className="section-contents">
                        <DriversSectionContents
                            interaction={interaction}
                            interactionId={interaction._id}
                            vehicleIndex={vehicleIndex}
                            disableLinks={this.disableLinks()}
                            canPerformMta={!this.props.annualPolicyInfo.maxMtaDateReached}
                        />
                        {driversCount < this.maxDriversCount && <SectionLinks>
                            <ButtonLink
                                id={`add-another-driver-${vehicleIndex}`}
                                primary={true}
                                to={`/journey/init/product/AnnualMotorInsurance/process/mtaAddPermanentDriver/preset/${interaction._id}`}
                                disabled={this.isDisabled()}
                                alertMessage={this.alertMessage}
                            >Add another driver</ButtonLink>
                        </SectionLinks>}
                    </div>
                </Section>
                <Section className="excess-section">
                    <div className="mobile-heading">
                        <SectionHeading className="position-relative mb-0 d-flex">
                            Excess that apply
                            <InfoModal
                                className="ml-2"
                                title="Excess explained"
                            >
                                <ExcessExplanation vehicle={this.vehicle}/>
                            </InfoModal>
                        </SectionHeading>
                    </div>
                    <SectionContents>
                        <Detail>
                            <DetailKey>
                                Accidental damage:
                            </DetailKey>
                            <DetailValuePrice>
                                {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.compulsoryExcess + this.vehicle.annualVehicleExcess.voluntaryExcess)} (this includes {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.voluntaryExcess)} voluntary excess)
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"pt-0"}>
                            <DetailKey>
                                Fire or theft:
                            </DetailKey>
                            <DetailValuePrice>
                                {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.compulsoryExcess + this.vehicle.annualVehicleExcess.voluntaryExcess)} (this includes {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.voluntaryExcess)} voluntary excess)
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"font-weight-bold"}>
                            Glass-only excess costs you less
                        </Detail>
                        <Detail className={"pt-0"}>
                            <DetailKey>
                                Glass repair excess:
                            </DetailKey>
                            <DetailValuePrice>
                                <span
                                    className="font-weight-normal">just</span> {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.windscreenRepairExcess)}
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"pt-0"}>
                            <DetailKey>
                                Glass replacement excess:
                            </DetailKey>
                            <DetailValuePrice>
                                <span
                                    className="font-weight-normal">just</span> {getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.windscreenReplacementExcess)}
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"non-recommended-excess"}>
                            <DetailValue>
                                <div className="font-weight-bold">Non-recommended repairer excess costs you more
                                </div>
                            </DetailValue>
                            <DetailKey>Non-recommended repairer excess:</DetailKey>
                            <DetailValue>
                                <span
                                    className="font-weight-bold">{getAmountWithoutDecimalDigits(this.vehicle.annualVehicleExcess.nonRecommendedRepairerExcess)}</span>
                            </DetailValue>
                            <div>This is in addition to the excesses shown above if you choose a repairer <span
                                className="font-weight-bold">not</span> on our list of recommended repairers.<br/>
                                This also applies to glass replacement but <span
                                    className="font-weight-bold">not</span> glass-only repairs.
                            </div>
                        </Detail>
                    </SectionContents>
                </Section>
                <Section className="optional-extras-section">
                    <div className="mobile-heading">
                        <SectionHeading className="mb-3">Optional extras</SectionHeading>
                    </div>
                    <SectionContents>
                        <Detail>
                            <DetailKey>
                                Protected no claim discount:
                            </DetailKey>
                            <DetailValue>
                                {this.interactionToRender.policyHolder.ncd.doNotDisclose ? "Included" : "Not included"}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Motor legal expenses:
                            </DetailKey>
                            <DetailValue>
                                {getIsOptionalExtraIncludedText(this.interactionToRender.coverTier, this.interactionToRender.optionalExtras.motorLegalExpenses)}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Guaranteed hire car:
                            </DetailKey>
                            <DetailValue>
                                {getIsOptionalExtraIncludedText(this.interactionToRender.coverTier, this.interactionToRender.optionalExtras.courtesyCar)}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <SectionLinks>
                                <ButtonLink
                                    id={`edit-optional-extras-${vehicleIndex}`}
                                    aria-label="Edit optional extras"
                                    primary={true}
                                    to={`/journey/init/product/AnnualMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${interaction._id}`}
                                    disabled={this.isDisabled()}
                                    alertMessage={this.alertMessage}
                                >Edit optional extras</ButtonLink>
                            </SectionLinks>
                        </Detail>
                    </SectionContents>
                </Section>
                <Section className="green-card-section">
                    <div className="mobile-heading">
                        <SectionHeading className="mb-3">Green card</SectionHeading>
                    </div>
                    <SectionContents>
                        <Detail>
                            <DetailValue>
                                {this.getGreenCardDescription()}
                            </DetailValue>
                        </Detail>
                    </SectionContents>
                </Section>
            </div>
        );
    }

    private isDisabled() {
        return this.props.annualPolicyInfo.maxMtaDateReached || this.disableLinks();
    }

    private disableLinks(): boolean {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange, maxFutureMtaDate} = this.props.annualPolicyInfo;
        const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(maxFutureMtaDate), 'day');
        return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange || mtaOnEndDateOfPolicy;
    }

    private getGreenCardDescription = (): React.ReactNode => {
        if (this.state.greenCards.length === 0) {
            return (
                <>
                    Did you know that UK are now part of the <strong>Green Card Free Circulation Area</strong>?
                    This now means that if you are driving within the EEA (European Economic Area) you no longer need to
                    carry with you a green card.
                    That’s one less thing to worry about.
                </>
            );
        } else {
            return (
                <>
                    Your Green Card for{" "}
                    {capitalize(this.vehicle.make)}, {capitalize(this.vehicle.model)} ({this.vehicle.registrationNumber}){" "}
                    {this.state.greenCards[0].caravanOrTrailer && (
                        <>and Trailer or Caravan ({this.state.greenCards[0].caravanDescription}){" "}</>
                    )}
                    is available in{" "}
                    <Link
                        to="/dashboard/annualMotorDocuments"
                        className="text-blue-1 font-weight-semi-bold"
                    >
                        '<span className="text-decoration-underline">Your documents</span>'
                    </Link>{" "}
                    for you to print at home on white paper.
                    Remember to carry it with you on the trip with your other documents.
                </>
            );
        }
    }

}
